import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usersApi, areasApi, authApi } from "@/lib/api";
import { toast } from "sonner";
import { PencilSimple, UserCircle, UsersThree, Plus, X, Lock, Eye, EyeSlash } from "@phosphor-icons/react";
import PushNotificationToggle from "@/components/PushNotificationToggle";
import WalletCard from "./WalletCard";

export default function ProfileView() {
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [areas, setAreas] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [linked, setLinked] = useState([]);
  const [activeMembers, setActiveMembers] = useState([]);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkSearch, setLinkSearch] = useState("");
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdShow, setPwdShow] = useState({ current: false, next: false });
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => {
    areasApi.list().then(({ data }) => setAreas(data)).catch(() => {});
    usersApi.getMyLinked().then(({ data }) => setLinked(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setForm({
      phone: user.phone || "", clanName: user.clanName || "", familyName: user.familyName || "",
      areaId: user.areaId || "", spouseName: user.spouseName || "", spouseAge: user.spouseAge || "",
      nextOfKinName: user.nextOfKinName || "", nextOfKinPhone: user.nextOfKinPhone || "",
    });
  }, [user]);

  // Uncontrolled inputs via a single ref → no focus loss mid-typing.
  const formRef = useRef({});

  useEffect(() => {
    // Seed formRef when user is loaded / changes
    if (user) {
      formRef.current = {
        phone: user.phone || "",
        clanName: user.clanName || "",
        familyName: user.familyName || "",
        spouseName: user.spouseName || "",
        spouseAge: user.spouseAge || "",
        nextOfKinName: user.nextOfKinName || "",
        nextOfKinPhone: user.nextOfKinPhone || "",
        areaId: user.areaId || "",
      };
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const f = formRef.current;
      await usersApi.updateProfile({ ...f, spouseAge: f.spouseAge ? Number(f.spouseAge) : undefined });
      await refreshUser();
      toast.success("Profile updated");
      setEditing(false);
    } catch { toast.error("Failed to update profile"); }
    finally { setSaving(false); }
  };

  const handleLink = async (memberId) => {
    try {
      await usersApi.linkAccount(user.id, memberId);
      toast.success("Account linked!");
      setShowLinkDialog(false);
      usersApi.getMyLinked().then(({ data }) => setLinked(data));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to link"); }
  };

  const handleUnlink = async (linkedId) => {
    try {
      await usersApi.unlinkAccount(user.id, linkedId);
      toast.success("Account unlinked");
      usersApi.getMyLinked().then(({ data }) => setLinked(data));
    } catch { toast.error("Failed to unlink"); }
  };

  const handleChangePassword = async (e) => {
    e?.preventDefault?.();
    if (pwd.next.length < 4) { toast.error("New password must be at least 4 characters"); return; }
    if (pwd.next !== pwd.confirm) { toast.error("New passwords do not match"); return; }
    if (pwd.next === pwd.current) { toast.error("New password must differ from current"); return; }
    setPwdSaving(true);
    try {
      await authApi.changePassword(pwd.current, pwd.next);
      toast.success("Password updated");
      setPwd({ current: "", next: "", confirm: "" });
      setPwdOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally {
      setPwdSaving(false);
    }
  };

  const loadMembers = () => {
    if (activeMembers.length === 0) {
      // Load from directory - only admin can list all members, but members can search
      // For now just use the directory endpoint
      import("@/lib/api").then(({ directoryApi }) => {
        directoryApi.members().then(({ data }) => setActiveMembers(data)).catch(() => {});
      });
    }
    setShowLinkDialog(true);
  };

  if (!user) return null;
  const areaName = areas.find(a => a.id === user.areaId)?.name || "—";

  if (editing) {
    return (
      <div className="space-y-5" data-testid="profile-edit">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Edit Profile</h2>
          <button onClick={() => setEditing(false)} className="text-sm text-muted-foreground underline">Cancel</button>
        </div>
        <div className="bg-card border border-border rounded-xl p-6 space-y-4">
          {[
            { label: "Phone (M-Pesa)", key: "phone", type: "tel" },
            { label: "Clan Name", key: "clanName" },
            { label: "Family Name", key: "familyName" },
            { label: "Spouse Name", key: "spouseName" },
            { label: "Spouse Age", key: "spouseAge", type: "number" },
            { label: "Next of Kin", key: "nextOfKinName" },
            { label: "Next of Kin Phone", key: "nextOfKinPhone", type: "tel" },
          ].map(({ label, key, type }) => (
            <div key={key}>
              <label className="text-sm font-medium text-foreground block mb-1">{label}</label>
              <input
                type={type || "text"}
                defaultValue={formRef.current[key] || ""}
                onChange={(e) => { formRef.current[key] = e.target.value; }}
                data-testid={`profile-edit-${key}`}
                className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          ))}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1">Area</label>
            <select
              defaultValue={formRef.current.areaId || ""}
              onChange={(e) => { formRef.current.areaId = e.target.value; }}
              className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl"
            >
              <option value="">Select area</option>
              {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <button onClick={handleSave} disabled={saving} data-testid="save-profile-btn" className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 transition-transform disabled:opacity-60">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="profile-view">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-semibold tracking-tight">My Profile</h2>
        <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-sm text-primary font-medium" data-testid="edit-profile-btn">
          <PencilSimple className="w-4 h-4" /> Edit
        </button>
      </div>

      {/* Push notification opt-in */}
      <PushNotificationToggle />

      {/* Wallet */}
      <WalletCard />

      {/* Profile card */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <UserCircle className="w-10 h-10 text-primary" />
          </div>
          <div>
            <p className="font-semibold text-lg">{user.name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-mono text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded">{user.ukcNumber || user.memberNumber || "Pending"}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                user.status === "active" ? "bg-green-100 text-green-700" :
                user.status === "probation" ? "bg-purple-100 text-purple-700" :
                user.status === "approved" ? "bg-blue-100 text-blue-700" :
                "bg-yellow-100 text-yellow-700"
              }`}>{user.status}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm border-t border-border pt-4">
          {[
            { label: "Phone", value: user.phone },
            { label: "Clan", value: user.clanName },
            { label: "Family", value: user.familyName },
            { label: "Area", value: areaName },
            { label: "Spouse", value: user.spouseName ? `${user.spouseName}${user.spouseAge ? ` (${user.spouseAge} yrs)` : ""}` : undefined },
            { label: "Next of Kin", value: user.nextOfKinName },
            { label: "Next of Kin Phone", value: user.nextOfKinPhone },
            { label: "Registered", value: user.registrationDate ? new Date(user.registrationDate).toLocaleDateString() : undefined },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-muted-foreground text-xs">{label}</p>
              <p className="font-medium">{value || "—"}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Linked Accounts */}
      <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="linked-accounts-card">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UsersThree className="w-5 h-5 text-primary" weight="duotone" />
            <p className="font-heading font-semibold text-sm">Linked Accounts</p>
          </div>
          <button onClick={loadMembers} data-testid="link-account-btn" className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-medium active:scale-95">
            <Plus className="w-3.5 h-3.5" /> Link
          </button>
        </div>
        {linked.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">
            <p>No linked accounts yet.</p>
            <p className="text-xs mt-1">Link accounts to make bulk payments for family members.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {linked.map(l => (
              <div key={l.id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{l.name}</p>
                  <p className="text-xs text-muted-foreground">{l.ukcNumber || l.memberNumber} · {l.phone}</p>
                </div>
                <button onClick={() => handleUnlink(l.id)} className="p-1.5 text-destructive hover:bg-destructive/10 rounded-lg" data-testid={`unlink-${l.id}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Change Password */}
      <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="change-password-card">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" weight="duotone" />
            <p className="font-heading font-semibold text-sm">Password</p>
          </div>
          {!pwdOpen && (
            <button
              type="button"
              onClick={() => setPwdOpen(true)}
              data-testid="open-change-password-btn"
              className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-medium active:scale-95"
            >
              <PencilSimple className="w-3.5 h-3.5" /> Change
            </button>
          )}
        </div>
        {!pwdOpen ? (
          <div className="px-5 py-4 text-sm text-muted-foreground">
            Use a strong password you don't reuse anywhere else. Minimum 4 characters.
          </div>
        ) : (
          <form onSubmit={handleChangePassword} className="px-5 py-4 space-y-3" data-testid="change-password-form">
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Current password</label>
              <div className="relative">
                <input
                  type={pwdShow.current ? "text" : "password"}
                  value={pwd.current}
                  onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
                  className="w-full px-3 py-2 pr-10 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="pwd-current-input"
                  autoComplete="current-password"
                  required
                />
                <button type="button" onClick={() => setPwdShow((s) => ({ ...s, current: !s.current }))} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground" tabIndex={-1}>
                  {pwdShow.current ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">New password</label>
              <div className="relative">
                <input
                  type={pwdShow.next ? "text" : "password"}
                  value={pwd.next}
                  onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
                  className="w-full px-3 py-2 pr-10 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="pwd-new-input"
                  autoComplete="new-password"
                  minLength={4}
                  required
                />
                <button type="button" onClick={() => setPwdShow((s) => ({ ...s, next: !s.next }))} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground" tabIndex={-1}>
                  {pwdShow.next ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Confirm new password</label>
              <input
                type={pwdShow.next ? "text" : "password"}
                value={pwd.confirm}
                onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="pwd-confirm-input"
                autoComplete="new-password"
                minLength={4}
                required
              />
              {pwd.confirm && pwd.next !== pwd.confirm && (
                <p className="text-xs text-destructive mt-1">Passwords do not match.</p>
              )}
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={pwdSaving || !pwd.current || !pwd.next || pwd.next !== pwd.confirm}
                data-testid="pwd-submit-btn"
                className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 transition-transform disabled:opacity-60"
              >
                {pwdSaving ? "Updating…" : "Update password"}
              </button>
              <button
                type="button"
                onClick={() => { setPwdOpen(false); setPwd({ current: "", next: "", confirm: "" }); }}
                disabled={pwdSaving}
                data-testid="pwd-cancel-btn"
                className="px-4 py-2.5 rounded-xl bg-secondary text-secondary-foreground font-medium text-sm border border-border"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Link Account Dialog */}
      {showLinkDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-xl border border-border w-full max-w-md max-h-[80vh] flex flex-col" data-testid="link-account-dialog">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Link a Member</h3>
              <button onClick={() => setShowLinkDialog(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-3">
              <input value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} placeholder="Search by name..." className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="link-search" />
            </div>
            <div className="flex-1 overflow-y-auto px-6 pb-4 divide-y divide-border">
              {activeMembers
                .filter(m => m.id !== user.id && !linked.some(l => l.id === m.id))
                .filter(m => !linkSearch.trim() || m.name.toLowerCase().includes(linkSearch.toLowerCase()))
                .map(m => (
                  <button key={m.id} onClick={() => handleLink(m.id)} className="w-full flex items-center justify-between py-3 text-left hover:bg-secondary/30 transition-colors" data-testid={`link-member-${m.id}`}>
                    <div>
                      <p className="text-sm font-medium">{m.name}</p>
                      <p className="text-xs text-muted-foreground">{m.memberNumber}</p>
                    </div>
                    <Plus className="w-4 h-4 text-primary" />
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
